import type {
  AuthService,
  ChatRoleService,
  ChatStorage,
  LecturaConfig,
  LecturaHostAdapter,
  NotesStorage,
  RatingStorage,
  ResourcesStorage,
  AIResourcesService
} from '../core/types';
import * as authStub from './auth-stub';
import { createFirebaseAuthService } from './auth-firebase';
import { createFirestoreChatStorage } from './chat-firestore';
import { createFirestoreChatRoleService } from './chat-role-firestore';
import { createStubChatRoleService } from './chat-role-stub';
import { createFirestoreNotesStorage } from './notes-firestore';
import { createFirestoreRatingStorage } from './rating-firestore';
import { chatStorage as stubChatStorage, notesStorage as stubNotesStorage } from './storage-stub';
import { recordRating, getMyRating } from './rating-stub';
import { installFirestoreSettingsSync } from './settings-firestore-sync';
import { createFirestoreResourcesStorage } from './resources-firestore';
import { resourcesStorageStub } from './resources-stub';
import { createFirestoreAIResourcesService, aiResourcesServiceStub } from './ai-resources';
import { createChatMentionNotifyStub, createFirestoreChatMentionNotifyService } from './chat-mention-notify';
import type { ChatMentionNotifyService } from './chat-mention-notify';
import {
  createFirestorePresentationManagementService,
  createStubPresentationManagementService,
  type PresentationManagementService
} from './presentation-firestore';
import { warnLecturaNonProduction } from '../lib/lectura-non-production-log';

export interface RuntimeDependencies {
  authService: AuthService;
  chatStorage: ChatStorage;
  notesStorage: NotesStorage;
  ratingStorage: RatingStorage;
  resourcesStorage: ResourcesStorage;
  aiResourcesService: AIResourcesService;
  chatMentionNotify: ChatMentionNotifyService;
  presentationManagementService: PresentationManagementService;
  wrappedAdapter: LecturaHostAdapter;
  chatRoleService: ChatRoleService;
}

export function createRuntimeDependencies(
  config: LecturaConfig,
  adapter: LecturaHostAdapter,
  drawerOnly: boolean
): RuntimeDependencies {
  const firebaseConfig =
    config.firebaseConfig ??
    (config.chat && (config.chat as { firebaseConfig?: unknown }).firebaseConfig) ??
    (typeof window !== 'undefined' && (window as { __LECTURA_FIREBASE_CONFIG__?: unknown }).__LECTURA_FIREBASE_CONFIG__) ??
    null;
  const hasFirebase = !!firebaseConfig && typeof firebaseConfig === 'object';
  const useFirebaseAuth = hasFirebase;
  const useFirestoreChat = hasFirebase;

  const chatStorage: ChatStorage = useFirestoreChat
    ? createFirestoreChatStorage({
        firebaseConfig,
        chatConfig: config.chat
      })
    : stubChatStorage;
  const notesStorage: NotesStorage = useFirestoreChat
    ? createFirestoreNotesStorage({
        firebaseConfig,
        chatConfig: config.chat
      })
    : stubNotesStorage;

  let authService: AuthService;
  if (useFirebaseAuth) {
    authService = createFirebaseAuthService(firebaseConfig);
  } else if (drawerOnly) {
    authService = {
      getCurrentUser: () => adapter.getCurrentUser(),
      subscribeUserChange: (cb) => adapter.subscribeUserChange(cb),
      signInGoogle: adapter.signInGoogle,
      signInGithub: adapter.signInGithub
    };
  } else {
    authService = {
      getCurrentUser: authStub.getCurrentUser,
      signInGoogle: authStub.signInGoogle,
      signInGithub: authStub.signInGithub,
      signOut: authStub.signOut,
      deleteCurrentUser: authStub.deleteCurrentUser,
      subscribeUserChange: authStub.subscribeUserChange
    };
  }

  const ratingStorage: RatingStorage = useFirestoreChat
    ? createFirestoreRatingStorage({
        firebaseConfig,
        chatConfig: config.chat,
        getCurrentUser: () => authService.getCurrentUser()
      })
    : { recordRating, getMyRating };
  const resourcesStorage: ResourcesStorage = useFirestoreChat
    ? createFirestoreResourcesStorage({
        firebaseConfig,
        chatConfig: config.chat
      })
    : resourcesStorageStub;
  const aiResourcesService: AIResourcesService = useFirestoreChat
    ? createFirestoreAIResourcesService()
    : aiResourcesServiceStub;
  const chatMentionNotify: ChatMentionNotifyService = useFirestoreChat
    ? createFirestoreChatMentionNotifyService()
    : createChatMentionNotifyStub();
  const presentationManagementService: PresentationManagementService = useFirestoreChat
    ? createFirestorePresentationManagementService({
        firebaseConfig,
        chatConfig: config.chat,
        getCurrentUser: () => authService.getCurrentUser()
      })
    : createStubPresentationManagementService();

  const wrappedAdapter = (drawerOnly && !useFirebaseAuth)
    ? adapter
    : { ...adapter, getCurrentUser: () => authService.getCurrentUser() };
  const chatRoleService = useFirestoreChat
    ? createFirestoreChatRoleService({
        firebaseConfig,
        chatConfig: config.chat,
        getCurrentUser: () => authService.getCurrentUser(),
        getPresId: () => wrappedAdapter.getPresId()
      })
    : createStubChatRoleService({
        getCurrentUser: () => authService.getCurrentUser()
      });

  if (useFirebaseAuth) {
    installFirestoreSettingsSync(authService, {
      firebaseConfig,
      chatConfig: config.chat
    });
  }

  if (!hasFirebase) {
    warnLecturaNonProduction(
      'No Firebase config: chat, notes, rating, resources, AI resources, @mention notify, presentation management, and chat roles use in-memory/local stubs.',
      {
        drawerOnly,
        auth: drawerOnly ? 'host-adapter (storage still stub)' : 'in-memory stub (auth-stub.ts)'
      }
    );
  }

  return {
    authService,
    chatStorage,
    notesStorage,
    ratingStorage,
    resourcesStorage,
    aiResourcesService,
    chatMentionNotify,
    presentationManagementService,
    wrappedAdapter,
    chatRoleService
  };
}
